import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";

import type { ContractAppProfileEntity } from "src/app/features/Contract/types/subTypes/ContractAppProfileEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { useContractProfileLoadQuery, useContractProfileUpdateMutation } from "../../services/server/ContractProfileApi";
import type { ContractProfileEntity } from "../../types/ContractProfileEntity";

export const ContractProfileEditForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { id } = useParams();
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.CONTRACT_PROFILES}`);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-profile");

    const [formData, setFormData] = useState<ContractProfileEntity>({} as ContractProfileEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useContractProfileLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractProfileUpdateMutation();
    const dispatch = useDispatch();

    const shouldSaveContractAppProfile = ({ acceptMode, routingId, readOnly, hostOperatorId }: ContractAppProfileEntity): boolean => {
        return !!acceptMode || (!acceptMode && !routingId && !readOnly && !hostOperatorId);
    };

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // Only send if valid and filled
            // send warning notification if invalid (valid if acceptMode is defined or if it is empty)
            if (formData.acceptedCardProfiles?.some((item) => !shouldSaveContractAppProfile(item))) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("contract.error.contractAppProfiles"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            // only save acceptedCardProfiles if it has acceptedMode defined, not if it is empty
            const updatedFormData = {
                ...formData,
                acceptedCardProfiles: formData.acceptedCardProfiles?.filter((item) => !!item.acceptMode),
            };

            const result = await saveFormData({ data: updatedFormData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(i18n.t("common.formUpdateSuccess", { id: result.data.id, resource: "ContractProfile" }));
            }
        },
        [saveFormData, formData, completeSubmit, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<ContractProfileEntity>
                    name="ContractProfileEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
